/**
 * Web Bluetooth API Service — Browser-side BLE communication with EV controllers.
 * Per-brand BLE service/characteristic UUID maps.
 * Requires HTTPS and Chrome/Edge/Opera.
 */

// Per-brand BLE UUID maps
const BLE_PROFILES = {
  fardriver: {
    serviceUUID: '0000ffe0-0000-1000-8000-00805f9b34fb',
    txCharUUID: '0000ffe1-0000-1000-8000-00805f9b34fb',
    rxCharUUID: '0000ffe2-0000-1000-8000-00805f9b34fb',
    nameFilters: ['FarDriver', 'FD-', 'ND'],
    writeMode: 'withoutResponse',
  },
  sabvoton: {
    serviceUUID: '0000fff0-0000-1000-8000-00805f9b34fb',
    txCharUUID: '0000fff1-0000-1000-8000-00805f9b34fb',
    rxCharUUID: '0000fff2-0000-1000-8000-00805f9b34fb',
    nameFilters: ['Sabvoton', 'SVMC', 'SV-'],
    writeMode: 'withResponse',
  },
  kelly: {
    serviceUUID: '0000fff0-0000-1000-8000-00805f9b34fb',
    txCharUUID: '0000fff1-0000-1000-8000-00805f9b34fb',
    rxCharUUID: '0000fff2-0000-1000-8000-00805f9b34fb',
    nameFilters: ['Kelly', 'KLS'],
    writeMode: 'withResponse',
  },
  ko_moto: {
    serviceUUID: '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service
    txCharUUID: '6e400003-b5a3-f393-e0a9-e50e24dcca9e',
    rxCharUUID: '6e400002-b5a3-f393-e0a9-e50e24dcca9e',
    nameFilters: ['KO-', 'KO Moto'],
    writeMode: 'withoutResponse',
  },
  ebmx: {
    serviceUUID: '0000ffe0-0000-1000-8000-00805f9b34fb',
    txCharUUID: '0000ffe1-0000-1000-8000-00805f9b34fb',
    rxCharUUID: '0000ffe2-0000-1000-8000-00805f9b34fb',
    nameFilters: ['EBMX', 'X-9000'],
    writeMode: 'withResponse',
  },
  voltol: {
    serviceUUID: '0000fff0-0000-1000-8000-00805f9b34fb',
    txCharUUID: '0000fff1-0000-1000-8000-00805f9b34fb',
    rxCharUUID: '0000fff2-0000-1000-8000-00805f9b34fb',
    nameFilters: ['Voltol', 'EM-'],
    writeMode: 'withResponse',
  },
  votol: {
    serviceUUID: '0000ffe0-0000-1000-8000-00805f9b34fb',
    txCharUUID: '0000ffe1-0000-1000-8000-00805f9b34fb',
    rxCharUUID: '0000ffe2-0000-1000-8000-00805f9b34fb',
    nameFilters: ['Votol', 'VOT-'],
    writeMode: 'withoutResponse',
  },
};

// Fallback profile
const DEFAULT_PROFILE = {
  serviceUUID: '0000fff0-0000-1000-8000-00805f9b34fb',
  txCharUUID: '0000fff1-0000-1000-8000-00805f9b34fb',
  rxCharUUID: '0000fff2-0000-1000-8000-00805f9b34fb',
  nameFilters: [],
  writeMode: 'withResponse',
};

class BluetoothService {
  constructor() {
    this.device = null;
    this.server = null;
    this.txCharacteristic = null;
    this.rxCharacteristic = null;
    this.isConnected = false;
    this.onData = null;
    this.onDisconnect = null;
    this._readBuffer = [];
    this.brand = null;
    this.profile = DEFAULT_PROFILE;
  }

  static isSupported() {
    return 'bluetooth' in navigator;
  }

  async connect(brandId) {
    if (!BluetoothService.isSupported()) {
      throw new Error('Web Bluetooth not supported. Use Chrome or Edge.');
    }

    this.brand = brandId;
    this.profile = BLE_PROFILES[brandId] || DEFAULT_PROFILE;

    try {
      const prefixes = this.profile.nameFilters;

      this.device = await navigator.bluetooth.requestDevice({
        filters: prefixes.length > 0
          ? prefixes.map(p => ({ namePrefix: p }))
          : [{ services: [this.profile.serviceUUID] }],
        optionalServices: [this.profile.serviceUUID, 'generic_access'],
      });

      this.device.addEventListener('gattserverdisconnected', () => {
        this.isConnected = false;
        if (this.onDisconnect) this.onDisconnect();
      });

      this.server = await this.device.gatt.connect();

      try {
        const service = await this.server.getPrimaryService(this.profile.serviceUUID);

        this.txCharacteristic = await service.getCharacteristic(this.profile.txCharUUID);
        await this.txCharacteristic.startNotifications();
        this.txCharacteristic.addEventListener('characteristicvaluechanged', (event) => {
          const bytes = Array.from(new Uint8Array(event.target.value.buffer));
          this._readBuffer.push(...bytes);
          if (this.onData) this.onData(bytes);
        });

        this.rxCharacteristic = await service.getCharacteristic(this.profile.rxCharUUID);
      } catch {
        console.warn('Controller BLE service not found, connected in raw mode');
      }

      this.isConnected = true;

      return {
        success: true,
        deviceName: this.device.name || 'Unknown',
        deviceId: this.device.id,
        hasService: !!this.txCharacteristic,
        profile: this.brand,
      };
    } catch (err) {
      this.isConnected = false;
      if (err.name === 'NotFoundError') {
        throw new Error('No Bluetooth device selected. Try again and pick your controller.');
      }
      throw err;
    }
  }

  async sendBytes(bytes) {
    if (!this.rxCharacteristic) throw new Error('BLE write characteristic not available');

    const chunk = 20; // BLE MTU limit
    for (let i = 0; i < bytes.length; i += chunk) {
      const slice = bytes.slice(i, i + chunk);
      const data = new Uint8Array(slice);

      if (this.profile.writeMode === 'withoutResponse') {
        await this.rxCharacteristic.writeValueWithoutResponse(data);
      } else {
        await this.rxCharacteristic.writeValueWithResponse(data);
      }
    }
  }

  async sendAndReceive(bytes, timeoutMs = 3000) {
    this._readBuffer = [];
    await this.sendBytes(bytes);

    return new Promise((resolve) => {
      const startTime = Date.now();
      const check = () => {
        if (this._readBuffer.length > 0) {
          setTimeout(() => {
            const response = [...this._readBuffer];
            this._readBuffer = [];
            resolve(response);
          }, 200);
        } else if (Date.now() - startTime > timeoutMs) {
          resolve([]);
        } else {
          setTimeout(check, 50);
        }
      };
      check();
    });
  }

  async readParams(readCommand, paramAddresses) {
    const response = await this.sendAndReceive(readCommand, 3000);

    if (response.length === 0) {
      return { success: false, raw: [], error: 'No BLE response (timeout)' };
    }

    const params = {};
    const addrMap = {};
    Object.entries(paramAddresses).forEach(([name, addr]) => {
      addrMap[addr] = name;
    });

    for (let i = 0; i < response.length - 2; i++) {
      if (addrMap[response[i]]) {
        params[addrMap[response[i]]] = (response[i + 1] << 8) | response[i + 2];
      }
    }

    return { success: true, params, raw: response, bytesReceived: response.length };
  }

  async writeParam(writePrefix, address, value) {
    const valueBytes = [(value >> 8) & 0xFF, value & 0xFF];
    const cmd = [...writePrefix, address, ...valueBytes];

    const response = await this.sendAndReceive(cmd, 2000);

    return {
      success: response.length > 0,
      sentBytes: cmd,
      response,
      ackReceived: response.length > 0,
    };
  }

  async disconnect() {
    if (this.txCharacteristic) {
      try { await this.txCharacteristic.stopNotifications(); } catch (e) { console.warn('BLE stop notify:', e.message); }
    }
    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }
    this.device = null;
    this.server = null;
    this.txCharacteristic = null;
    this.rxCharacteristic = null;
    this.isConnected = false;
    this._readBuffer = [];
  }
}

export default BluetoothService;
